import React, { useState, useEffect } from "react";
import {
  FaCog,
  FaBuilding,
  FaDollarSign,
  FaPhone,
  FaToggleOn,
  FaToggleOff,
  FaSave,
  FaUndo,
  FaTrash,
  FaPlus,
  FaEdit,
  FaCheck,
  FaTimes,
  FaDownload,
  FaUpload,
} from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("app"); // Default active tab
  const [settings, setSettings] = useState({});
  const [originalSettings, setOriginalSettings] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [newSetting, setNewSetting] = useState({ label: "", value: "" });
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Settings categories configuration
  const settingsCategories = {
    app: {
      title: "App Configuration",
      icon: FaCog,
      color: "blue",
      settings: [
        "app_name",
        "app_version",
        "company_name",
        "company_logo_url",
        "favicon_url",
      ],
    },
    business: {
      title: "Business Settings",
      icon: FaDollarSign,
      color: "green",
      settings: [
        "commission_rate",
        "cancellation_fee",
        "min_ride_amount",
        "max_ride_distance",
        "ride_timeout_minutes",
        "driver_rating_threshold",
      ],
    },
    contact: {
      title: "Contact & Support",
      icon: FaPhone,
      color: "purple",
      settings: [
        "support_email",
        "support_phone",
        "emergency_contact",
        "office_address",
        "website_url",
      ],
    },
    localization: {
      title: "Localization",
      icon: FaBuilding,
      color: "orange",
      settings: [
        "currency",
        "currency_symbol",
        "timezone",
        "default_language",
        "date_format",
      ],
    },
    features: {
      title: "Feature Toggles",
      icon: FaToggleOn,
      color: "indigo",
      settings: [
        "enable_scheduled_rides",
        "enable_multi_stop",
        "enable_ride_sharing",
        "maintenance_mode",
        "enable_notifications",
      ],
    },
  };

  useEffect(() => {
    fetchAllSettings();
  }, []);

  // Fetch all settings
  const fetchAllSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      console.log("Fetching settings from:", API.ADMIN.SETTINGS_ALL);

      const response = await axiosInstance.get(API.ADMIN.SETTINGS_ALL, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Settings API response:", response.data);

      if (response.data.success) {
        const settingsData = response.data.data?.settings || {};
        console.log("Parsed settings data:", settingsData);
        setSettings(settingsData);
        setOriginalSettings({ ...settingsData });
      } else {
        console.log("API returned success: false");
        toast.error(response.data.message || "Failed to fetch settings");
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      console.error("Error response:", error.response?.data);
      toast.error(
        "Failed to fetch settings - " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  // Update multiple settings
  const updateSettings = async (settingsToUpdate) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");

      // Use object format as per API documentation
      const payload = { settings: settingsToUpdate };

      console.log("Sending bulk settings update:");
      console.log("Payload:", JSON.stringify(payload, null, 2));

      const response = await axiosInstance.post(
        API.ADMIN.SETTINGS_UPDATE,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("Settings updated successfully");
        await fetchAllSettings();
      } else {
        console.error("API returned success: false", response.data);
        toast.error(response.data.message || "Failed to update settings");
      }
    } catch (error) {
      console.error("Error updating settings:", error);
      console.error("Error details:", error.response?.data);
      toast.error(
        "Failed to update settings - " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setSaving(false);
    }
  };

  // Update single setting
  const updateSingleSetting = async (label, value) => {
    try {
      const token = localStorage.getItem("adminToken");

      console.log(
        `Updating single setting: label="${label}", value="${value}"`
      );
      console.log("Label type:", typeof label, "Value type:", typeof value);

      // Validate inputs
      if (!label || label.trim() === "") {
        toast.error("Setting label is required");
        return;
      }

      if (value === undefined || value === null) {
        toast.error("Setting value is required");
        return;
      }

      // Use the dedicated single setting endpoint as per API docs
      const endpoint = `${API.ADMIN.SETTINGS_UPDATE_SINGLE}/${label}`;
      const payload = { value: value };

      console.log("Using single setting endpoint:", endpoint);
      console.log("Payload:", JSON.stringify(payload, null, 2));

      const response = await axiosInstance.put(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        toast.success(`Setting '${label}' updated successfully`);
        setSettings((prev) => ({ ...prev, [label]: value }));
        setEditingKey(null);
      } else {
        console.error("API returned success: false", response.data);
        toast.error(response.data.message || "Failed to update setting");
      }
    } catch (error) {
      console.error("Error updating setting:", error);
      console.error("Error response:", error.response?.data);
      toast.error(
        "Failed to update setting - " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  // Delete setting
  const deleteSetting = async (label) => {
    if (
      !window.confirm(`Are you sure you want to delete '${label}' setting?`)
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const response = await axiosInstance.delete(
        `${API.ADMIN.SETTINGS_DELETE}/${label}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Setting '${label}' deleted successfully`);
        await fetchAllSettings();
      }
    } catch (error) {
      console.error("Error deleting setting:", error);
      toast.error("Failed to delete setting");
    }
  };

  // Add new setting
  const addNewSetting = async () => {
    if (!newSetting.label.trim() || !newSetting.value.trim()) {
      toast.error("Both label and value are required");
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");

      console.log("Adding new setting:", newSetting);

      // Use object format for adding new setting
      const payload = {
        settings: {
          [newSetting.label.trim()]: newSetting.value.trim(),
        },
      };

      console.log(
        "Adding new setting - Payload:",
        JSON.stringify(payload, null, 2)
      );

      const response = await axiosInstance.post(
        API.ADMIN.SETTINGS_UPDATE,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("New setting added successfully");
        setShowAddModal(false);
        setNewSetting({ label: "", value: "" });
        await fetchAllSettings();
      } else {
        console.error("API returned success: false", response.data);
        toast.error(response.data.message || "Failed to add setting");
      }
    } catch (error) {
      console.error("Error adding setting:", error);
      console.error("Error response:", error.response?.data);
      toast.error(
        "Failed to add setting - " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  // Reset to defaults
  const resetToDefaults = async () => {
    if (
      !window.confirm(
        "Are you sure you want to reset all settings to defaults?"
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const response = await axiosInstance.post(
        API.ADMIN.SETTINGS_RESET,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("Settings reset to defaults successfully");
        await fetchAllSettings();
      }
    } catch (error) {
      console.error("Error resetting settings:", error);
      toast.error("Failed to reset settings");
    }
  };

  // Save category settings
  const saveCategorySettings = () => {
    const categorySettings = settingsCategories[activeTab]?.settings || [];
    const settingsToUpdate = {};

    categorySettings.forEach((key) => {
      if (
        settings[key] !== undefined &&
        settings[key] !== originalSettings[key]
      ) {
        settingsToUpdate[key] = settings[key];
      }
    });

    console.log("Category settings to update:", settingsToUpdate);

    if (Object.keys(settingsToUpdate).length > 0) {
      updateSettings(settingsToUpdate);
    } else {
      toast.info("No changes to save");
    }
  };

  // Handle setting value change
  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  // Test function to debug API calls
  const testApiCall = async () => {
    try {
      const token = localStorage.getItem("adminToken");

      console.log("=== COMPREHENSIVE API TEST ===");
      console.log("Base URL:", `${API.ADMIN.BASE}`);
      console.log("Token exists:", !!token);
      console.log(
        "Token preview:",
        token ? `${token.substring(0, 20)}...` : "None"
      );

      // Test different endpoints and payload combinations based on API docs
      const testCombinations = [
        {
          endpoint: API.ADMIN.SETTINGS_UPDATE,
          method: "POST",
          name: "SETTINGS_UPDATE with object format (recommended)",
          payload: {
            settings: {
              test_setting: "test_value",
            },
          },
        },
        {
          endpoint: API.ADMIN.SETTINGS_UPDATE,
          method: "POST",
          name: "SETTINGS_UPDATE with array format",
          payload: {
            settings: [
              {
                label: "test_setting_array",
                value: "test_value_array",
              },
            ],
          },
        },
        {
          endpoint: API.ADMIN.SETTINGS_BULK_UPDATE,
          method: "POST",
          name: "SETTINGS_BULK_UPDATE with array format",
          payload: {
            settings: [
              {
                label: "test_bulk_setting",
                value: "test_bulk_value",
              },
            ],
          },
        },
        {
          endpoint: `${API.ADMIN.SETTINGS_UPDATE_SINGLE}/test_single_setting`,
          method: "PUT",
          name: "SETTINGS_UPDATE_SINGLE (PUT method)",
          payload: {
            value: "test_single_value",
          },
        },
      ];

      // First, test basic connectivity
      try {
        console.log("\n=== TESTING BASIC CONNECTIVITY ===");
        const connectResponse = await axiosInstance.get(
          API.ADMIN.SETTINGS_ALL,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        console.log("✅ GET settings works:", connectResponse.data);
      } catch (error) {
        console.log(
          "❌ GET settings failed:",
          error.response?.data || error.message
        );
        console.log("Status:", error.response?.status);
        console.log("Headers:", error.response?.headers);

        if (error.response?.status === 401) {
          toast.error("Authentication failed - please login again");
          return;
        }
      }

      // Test all endpoint/payload combinations
      let successCount = 0;
      for (const test of testCombinations) {
        console.log(`\n--- Testing: ${test.name} ---`);
        console.log("Method:", test.method);
        console.log("Endpoint:", test.endpoint);
        console.log("Payload:", JSON.stringify(test.payload, null, 2));

        try {
          let response;
          const axiosConfig = {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          };

          // Use appropriate HTTP method
          if (test.method === "PUT") {
            response = await axiosInstance.put(
              test.endpoint,
              test.payload,
              axiosConfig
            );
          } else {
            response = await axiosInstance.post(
              test.endpoint,
              test.payload,
              axiosConfig
            );
          }

          console.log("✅ SUCCESS:", response.data);
          toast.success(`Test successful: ${test.name}`);
          successCount++;

          if (successCount === 1) {
            console.log("🎉 FOUND WORKING COMBINATION!");
            console.log("Working method:", test.method);
            console.log("Working endpoint:", test.endpoint);
            console.log(
              "Working payload structure:",
              JSON.stringify(test.payload, null, 2)
            );
          }
        } catch (error) {
          console.log("❌ FAILED:", {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            message: error.message,
          });
        }
      }

      if (successCount === 0) {
        console.log("\n🔍 ALL TESTS FAILED - DEBUGGING INFO:");
        console.log("Available API endpoints:", {
          SETTINGS_ALL: API.ADMIN.SETTINGS_ALL,
          SETTINGS_UPDATE: API.ADMIN.SETTINGS_UPDATE,
          SETTINGS_BULK_UPDATE: API.ADMIN.SETTINGS_BULK_UPDATE,
          SETTINGS_UPDATE_SINGLE: API.ADMIN.SETTINGS_UPDATE_SINGLE,
          SETTINGS_DELETE: API.ADMIN.SETTINGS_DELETE,
          SETTINGS_RESET: API.ADMIN.SETTINGS_RESET,
        });
        toast.error(`All ${testCombinations.length} test combinations failed`);
      } else {
        toast.success(
          `${successCount} out of ${testCombinations.length} tests passed`
        );
      }
    } catch (error) {
      console.error("Test API call failed:", error);
      console.error("Error response:", error.response?.data);
      toast.error("Test API call failed: " + error.message);
    }
  };

  // Handle toggle for boolean values
  const handleToggle = async (key) => {
    const currentValue = settings[key];
    const newValue =
      currentValue === true || currentValue === "true" ? false : true;

    // Update local state immediately for better UX
    handleSettingChange(key, newValue);

    // Save the toggle change immediately
    try {
      await updateSingleSetting(key, newValue);
    } catch (error) {
      // Revert the change if the API call failed
      handleSettingChange(key, currentValue);
    }
  };

  // Format setting label for display
  const formatLabel = (label) => {
    return label.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Check if setting is boolean type
  const isBooleanSetting = (key) => {
    const booleanSettings = [
      "enable_scheduled_rides",
      "enable_multi_stop",
      "enable_ride_sharing",
      "maintenance_mode",
      "enable_notifications",
    ];
    return booleanSettings.includes(key);
  };

  // Filter settings based on search term
  const getFilteredSettings = () => {
    const categorySettings = settingsCategories[activeTab]?.settings || [];
    const allSettingsKeys = Object.keys(settings);

    // If in a category tab, show category settings, otherwise show all
    const settingsToShow =
      activeTab === "all" ? allSettingsKeys : categorySettings;

    if (!searchTerm) return settingsToShow;

    return settingsToShow.filter(
      (key) =>
        key.toLowerCase().includes(searchTerm.toLowerCase()) ||
        formatLabel(key).toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Settings Management
            </h1>
            <p className="text-gray-600">
              Manage application configuration and settings
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <FaPlus className="mr-2" />
              Add Setting
            </button>
            {/* <button
              onClick={testApiCall}
              className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              <FaCog className="mr-2" />
              Test API
            </button> */}
            {/* <button
              onClick={resetToDefaults}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <FaUndo className="mr-2" />
              Reset to Defaults
            </button> */}
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search settings..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("all")}
            className={`flex items-center px-4 py-2 font-medium border-b-2 ${
              activeTab === "all"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            All Settings
          </button>
          {Object.entries(settingsCategories).map(([key, category]) => {
            const Icon = category.icon;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center px-4 py-2 font-medium border-b-2 ${
                  activeTab === key
                    ? `border-${category.color}-500 text-${category.color}-600`
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Icon className="mr-2" />
                {category.title}
              </button>
            );
          })}
        </div>
      </div>

      {/* Settings Content */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">
              {activeTab === "all"
                ? "All Settings"
                : settingsCategories[activeTab]?.title}
            </h2>
            {activeTab !== "all" && (
              <button
                onClick={saveCategorySettings}
                disabled={saving}
                className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 ${
                  saving ? "cursor-not-allowed" : ""
                }`}
              >
                <FaSave className="mr-2" />
                {saving ? "Saving..." : "Save Changes"}
              </button>
            )}
          </div>
        </div>

        <div className="p-6">
          {getFilteredSettings().length > 0 ? (
            <div className="space-y-4">
              {getFilteredSettings().map((key) => (
                <div key={key} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h3 className="text-lg font-medium text-gray-800">
                          {formatLabel(key)}
                        </h3>
                        <code className="ml-2 px-2 py-1 bg-gray-100 text-sm text-gray-600 rounded">
                          {key}
                        </code>
                      </div>

                      {editingKey === key ? (
                        <div className="flex items-center gap-2 mt-2">
                          {isBooleanSetting(key) ? (
                            <select
                              value={settings[key]?.toString() || "false"}
                              onChange={(e) =>
                                handleSettingChange(
                                  key,
                                  e.target.value === "true"
                                )
                              }
                              className="border rounded px-3 py-2"
                            >
                              <option value="true">Enabled</option>
                              <option value="false">Disabled</option>
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={settings[key] || ""}
                              onChange={(e) =>
                                handleSettingChange(key, e.target.value)
                              }
                              className="flex-1 border rounded px-3 py-2"
                              placeholder="Enter value"
                            />
                          )}
                          <button
                            onClick={() =>
                              updateSingleSetting(key, settings[key])
                            }
                            className="p-2 bg-green-600 text-white rounded hover:bg-green-700"
                          >
                            <FaCheck />
                          </button>
                          <button
                            onClick={() => {
                              setEditingKey(null);
                              setSettings((prev) => ({
                                ...prev,
                                [key]: originalSettings[key],
                              }));
                            }}
                            className="p-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ) : (
                        <div className="mt-2">
                          {isBooleanSetting(key) ? (
                            <div className="flex items-center">
                              <button
                                onClick={() => handleToggle(key)}
                                className="flex items-center"
                              >
                                {settings[key] === true ||
                                settings[key] === "true" ? (
                                  <FaToggleOn className="text-green-500 text-2xl" />
                                ) : (
                                  <FaToggleOff className="text-gray-400 text-2xl" />
                                )}
                                <span
                                  className={`ml-2 font-medium ${
                                    settings[key] === true ||
                                    settings[key] === "true"
                                      ? "text-green-600"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {settings[key] === true ||
                                  settings[key] === "true"
                                    ? "Enabled"
                                    : "Disabled"}
                                </span>
                              </button>
                            </div>
                          ) : (
                            <div className="text-gray-900 font-mono bg-gray-50 p-2 rounded">
                              {settings[key] || (
                                <span className="text-gray-400">Not set</span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 ml-4">
                      {editingKey !== key && (
                        <button
                          onClick={() => setEditingKey(key)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                      )}
                      <button
                        onClick={() => deleteSetting(key)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FaCog className="text-gray-400 text-4xl mx-auto mb-4" />
              <p className="text-gray-500 mb-4">
                {searchTerm
                  ? "No settings match your search"
                  : "No settings found in this category"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Add Setting Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add New Setting</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Setting Key
                </label>
                <input
                  type="text"
                  value={newSetting.label}
                  onChange={(e) =>
                    setNewSetting({ ...newSetting, label: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="e.g., max_users_per_ride"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Setting Value
                </label>
                <input
                  type="text"
                  value={newSetting.value}
                  onChange={(e) =>
                    setNewSetting({ ...newSetting, value: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Enter setting value"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={addNewSetting}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <FaSave className="mr-2" />
                Add Setting
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <FaTimes className="mr-2" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
